import * as _ from "lodash-es";
import { addHours } from 'date-fns';
import { isAfter, toDate } from 'src/app/shared/utils/dates';
import { AuthenticationClient } from "@feathersjs/authentication-client/lib";
import { AuthenticationResult } from "@feathersjs/authentication/lib";
import { Router } from "@angular/router";
import { KFeathers } from "./api/feathers.service";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { User } from "../models/users.model";
import { Injectable, OnDestroy, inject } from "@angular/core";
import {
    USER_TYPE_ADMIN,
    USER_TYPE_CPP,
    USER_TYPE_FAB,
    USER_TYPE_OPERATOR,
    USER_TYPE_CLIENT,
    USER_TYPE_ARCHIVER
} from "src/app/models/usersTypes.model";
import { environment } from "src/environments/environment";
import { CookieService } from "ngx-cookie-service";
import { Application } from "@feathersjs/feathers";
import { TranslateService } from "@ngx-translate/core";
import { UtilsService } from "./utils.service";
import { MarqueService } from "./marque.service";
import { Marque } from "../models/marque.model";
import { EZD_APP_REQUEST_TYPE, EZD_DISABLED_REQUEST_TYPE, RequestType } from "../LIVECO.define";

// cf-api.js (Cloudflow API client from Hybrid) is plain JavaScript without types
/* eslint-disable @typescript-eslint/no-require-imports */
const api = require("@modules/cf-api.js").api;
const api_async = require("@modules/cf-api.js").api_async;
/* eslint-enable @typescript-eslint/no-require-imports */

/**
 * Simple wrapper for feathers
 */
@Injectable({
    providedIn: "root"
})
export class AuthService<T extends User = User> implements OnDestroy {
    protected feathers = inject(KFeathers);
    protected router = inject(Router);
    protected cookies = inject(CookieService);
    protected translate = inject(TranslateService);
    protected s_marques = inject(MarqueService);

    //override this methode
    // @ts-ignore
    protected model: new (data: Partial<T>) => T = User;

    protected subscription: Subscription = new Subscription();

    public get authenticated(): boolean {
        return this.auth.authenticated;
    }
    public get onAuthenticated(): Observable<T | null> {
        return this._currentUser$.asObservable();
    }

    protected get app(): Application {
        return this.feathers.app;
    }
    protected get auth(): AuthenticationClient {
        return this.app.authentication;
    }

    protected get _currentUser(): T | null {
        return this._currentUser$.value;
    }
    protected _currentUser$: BehaviorSubject<T | null> = new BehaviorSubject<T | null>(null);
    protected _userBrand: Marque[] = [];

    get user() {
        if (!this._currentUser) {
            let partialUser: any = this.get("auth-user");
            if (partialUser) {
                this.user = partialUser;
            } else if (this._currentUser !== null) {
                this.user = null;
            }
        }
        return this._currentUser;
    }
    set user(u: T | null) {
        console.log("[SET USER]", u);
        try {
            this._currentUser$.next(u ? new this.model(u) : null);
            this.store("auth-user", this._currentUser);
        } catch (err) {
            console.error("[AUTH][ERROR]", err);
        }
    }

    get userBrand() {
        return this.user?.populated?.marques || [];
    }
    get hasEasyDiademFlag() {
        return this.userBrand.some((brand: any) => !!brand.flags?.easydiadem);
    }
    get requestTypes() {
        const requestTypesList = _.uniq(
            this.userBrand.flatMap((brand: Marque) => (_.isArray(brand?.flags?.easydiadem) ? brand?.flags?.easydiadem : []))
        );
        return !this.hasEasyDiademFlag
            ? []
            : requestTypesList.length
              ? requestTypesList.filter(value => !EZD_DISABLED_REQUEST_TYPE.includes(value))
              : EZD_APP_REQUEST_TYPE;
    }

    // get hasCustomerLimitedAccess() {
    //     if (this.isCPP() || this.isFab() || this.isOperator()) {
    //         return false;
    //     }
    //     if (this.user?.customer?.hasOption('superdev') || this.user?.customer?.hasOption('diadem') || this.user?.customer?.hasOption('full-access')) {
    //         return false;
    //     }

    //     return true;
    // }

    /**
     * Get current access token synchronious way
     * @return string
     */
    get accessToken() {
        return localStorage.getItem(this.auth.options.storageKey);
    }

    protected isBrowser: boolean = true;

    constructor() {
        if (this.feathers.isBrowser && this.feathers.socket) {
            // only use socket on the browser, not in the server while doing ssr
            // Auth the socket to the server to belong in 'authenticated' channel
            if (this.feathers.socket.disconnected && this.accessToken) {
                this.feathers.on("connect", () => {
                    this.feathers.emit(
                        "create",
                        "authentication",
                        {
                            strategy: "jwt",
                            accessToken: this.accessToken
                        },
                        async (error: any, newAuthResult: any) => {
                            if (newAuthResult) {
                                this.user = newAuthResult?.user;
                            }
                            console.log("[SOCKET LOGIN]", newAuthResult);
                            if (error) {
                                console.error("[SOCKET LOGIN]", error);
                            }
                        }
                    );
                });
            }
        }

        let session = localStorage.getItem("liveco-cf-session");
        if (session) {
            api.m_session = session;
            api_async.m_session = session;
        }

        // Retrieve current User
        // this.currentUser = await api_defer.auth.get_current_user();
        console.log("AUTH AS", this.user);

        this.subscription.add(
            this._currentUser$.subscribe(async user => {
                // if (this.authenticated && user?.marques) {
                //     await this.fetchUserBrands();
                // }
                if (user && user.lang && user.lang !== this.translate.getCurrentLang()) {
                    this.translate.use(user.lang);
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public async getUserRemote(): Promise<T> {
        const { user } = await this.app.get("authentication");
        console.log("[GET REMOTE USER]", user);
        return (this.user = user);
    }

    /**
     * Get current access token
     * @return string
     */
    public async getAccessToken(): Promise<string | null> {
        return await this.auth.getAccessToken();
    }

    /**
     * Set current access token
     */
    public async setAccessToken(token: string): Promise<string | null> {
        return await this.auth.setAccessToken(token);
    }

    /** Refresh existing token
     *  Important because service call wont be auth if the token isnt refreshed
     *  Only perfom call is the service isnt already auth
     */
    public async reAuthenticate(force?: boolean, strategy?: string): Promise<AuthenticationResult> {
        try {
            let result = await this.auth.reAuthenticate(force, strategy);
            this.app.set("authentication", result);

            this.user = result["user"];
            console.log("[reAUTH]", this.user);
            return result;
        } catch (err: any) {
            this.user = null;
            console.error(`[ERROR][reAUTH] ${err.message}`);
            throw new Error(`[ERROR][reAUTH] ${err.message}`, { cause: err });
        }
    }

    public async login(username: string, liveco_password: string) {
        console.log("LOGIN");
        const start = Date.now();

        // Clear all cookies
        const domain = `.${environment.api.domain}`;
        this.cookies.delete("csrf-token");
        this.cookies.delete("expiration_date");
        this.cookies.delete("user_hash");
        this.cookies.delete("user_id");

        // Clear local storage
        localStorage.removeItem("liveco-auth-user");
        localStorage.removeItem("liveco-cf-session-date");
        localStorage.removeItem("liveco-jwt");

        let credentials = { strategy: "local", username, liveco_password };
        let promise = this.feathers.authenticate(credentials);
        const { user } = await promise;

        this.user = user;
        // if (user?.marques) {
        //     this.userBrand = await this.s_marques.find();
        // }

        console.log("[LOGIN]", user);

        const end = Date.now();
        const duration = end - start;
        console.log(`The duration 1 was ${duration}ms`);

        // Disable login session in CF because we now use proofscope in anonymous mode
        if (this.user?.isDiadem) {
            await this.updateCFSession(username, liveco_password);
        }

        const endCF = Date.now();
        const durationCF = endCF - end;
        console.log(`The duration CF was ${durationCF}ms`);

        return promise;
    }

    async updateCFSession(username: string, liveco_password: string): Promise<void> {
        try {
            // let res = { session: '' };
            api.m_session = undefined;
            api_async.m_session = undefined;
            // res = api.auth.create_session(username, { password: liveco_password, expiry: 12 * 60 });
            // this.store('cf-session', res.session);
            this.store("cf-session-date", addHours(new Date(), 12).toISOString());
            // if (res && res.session) {
            //     this.store('cf-session', res.session);
            //     api.m_session = res.session;
            //     api_async.m_session = res.session;
            // }

            // Store COOKIE (for splitview) with wildcard domain name
            const cookie = api.auth.login(username, liveco_password);
            if (cookie.error) {
                console.error("[ERROR][CF-SESSION]", cookie.error);
                throw new Error(`CF Login ERROR : [${cookie.error_code}] ${cookie.error}`);
            } else if (cookie) {
                const expiry = toDate(cookie["expiry_time"]);
                const domain = `.${environment.api.domain}`;
                this.cookies.deleteAll("/", domain, true);
                this.cookies.set("user_id", cookie["user_id"], expiry, "/", domain, true);
                this.cookies.set("user_hash", cookie["user_hash"], expiry, "/", domain, true);
                this.cookies.set("expiration_date", cookie["expiration_date"], expiry, "/", domain, true);
                this.cookies.set("csrf-token", cookie["csrf_token"], expiry, "/", domain, true);
                await UtilsService.sleep(500);
                // Cookie.setCookie('user_id', cookie['user_id'], expiry, '/');
                // Cookie.setCookie('user_hash', cookie['user_hash'], expiry, '/');
                // Cookie.setCookie('expiration_date', cookie['expiration_date'], expiry, '/');
                // Cookie.setCookie('csrf-token', cookie['csrf_token'], expiry, '/');
            }
        } catch (err) {
            console.error("[ERROR][CF-SESSION]", err);
            throw err;
        }
    }

    hasCFSession(): boolean {
        return (
            !environment.production ||
            (this.cookies.check("user_id") &&
                this.cookies.check("user_hash") &&
                this.cookies.check("expiration_date") &&
                this.cookies.check("csrf-token") &&
                this.get("cf-session-date") &&
                isAfter(this.get("cf-session-date"), new Date()))
        );
        // && this.get('cf-session')
        // && this.get('cf-session-date')
        // && moment(this.get('cf-session-date')).isAfter(moment());
    }

    /**
     * Remove all data stored in localstorage
     */
    public logout() {
        try {
            this.remove("auth-user");
            this.remove("auth-brand");
            this.remove("user-request-types");
            this.remove("cf-session");
            this.remove("cf-session-date");
            this.feathers.logout();
            this.router.navigate(["/login"]);

            // // Remove COOKIE
            const domain = `.${environment.api.domain}`;
            this.cookies.deleteAll("/", domain, true);
            // this.cookies.delete('user_id');
            // this.cookies.delete('user_hash');
            // this.cookies.delete('expiration_date');
            // this.cookies.delete('csrf-token');
        } catch (err) {
            console.error("ERR", err);
        }
    }

    /** Handies */
    resetUser() {
        this.user = null;
    }

    /**
    //  * Check if user is root
    //  */
    // isRoot(): boolean {
    //   return this.hasRole('Root');
    // }

    // /**
    //  * Check if user is admin
    //  * @param roleName
    //  */
    // hasRole(roleName: string): boolean {
    //   let user: T | null = this.user;
    //   return user && user.role && _.isString(user.role.name) ? user.role.name === roleName : false;
    // }

    /**
     * Get data stored in LocalStorage
     * @param name
     */
    public get(name: string) {
        let item = localStorage.getItem(environment.slug + "-" + name);
        if (item) {
            try {
                return JSON.parse(item);
            } catch (err) {
                return item;
            }
        }
        return null;
    }

    /**
     * Remove data from Localstorage
     * @param name
     */
    public remove(name: string) {
        localStorage.removeItem(environment.slug + "-" + name);
    }

    /**
     * Store data in Localstorage
     * @param name
     * @param data
     */
    public store(name: string, data: any) {
        localStorage.setItem(environment.slug + "-" + name, _.isString(data) ? data : JSON.stringify(data));
    }

    isAdmin(): boolean {
        return this.hasRole([USER_TYPE_ADMIN]);
    }

    isCPP(): boolean {
        return this.hasRole([USER_TYPE_CPP, USER_TYPE_ADMIN]);
    }

    isFab(): boolean {
        return this.hasRole([USER_TYPE_FAB]);
    }

    isOperator(): boolean {
        return this.hasRole([USER_TYPE_OPERATOR]);
    }

    isClient(): boolean {
        return this.hasRole([USER_TYPE_CLIENT]);
    }

    hasRole(names: string[]): boolean {
        if (!this.user) return false;
        return names.some(name => this.user!.attributes_name.includes(name));
    }

    /**
     * FOR DISPLAY PURPOSE ONLY (NOT FOR SECURITY)
     * @returns
     */
    getUserType() {
        const user = this.user;
        const attributes_names = user?.attributes_name;
        if (user && attributes_names && attributes_names.length) {
            if (attributes_names.includes(USER_TYPE_ADMIN)) {
                return USER_TYPE_ADMIN;
            }
            if (attributes_names.includes(USER_TYPE_CPP)) {
                return USER_TYPE_CPP;
            }
            if (attributes_names.includes(USER_TYPE_FAB)) {
                return USER_TYPE_FAB;
            }
            if (attributes_names.includes(USER_TYPE_OPERATOR)) {
                return USER_TYPE_OPERATOR;
            }
            if (attributes_names.includes(USER_TYPE_CLIENT)) {
                return USER_TYPE_CLIENT;
            }
        }
        return "other";
    }

    /**
     * Check if user can archive
     * @returns
     *  */
    canArchive(): boolean {
        return this.hasRole([USER_TYPE_ARCHIVER]);
    }
}
